import { CustomCellEditorProps, useGridCellEditor } from "ag-grid-react";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { ExerciseAutocomplete } from "../../exercise-library/ExerciseAutocomplete";
import {
  AutocompleteRenderInputParams,
  Box,
  IconButton,
  TextField,
  createTheme,
} from "@mui/material";
import { ExerciseAsset } from "../../exercise-library/types";
import makeStyles from "@mui/styles/makeStyles";
import {
  CustomProgramCellEditorProps,
  getDefaultExerciseCellTitle,
} from "../utils";
import {
  ExerciseTypeExtra,
  ExerciseTypeReps,
  ExerciseTypeSet,
  RowType,
} from "../../../constants";
import { Video, ChevronDown } from "lucide-react";
import { WorkoutDrawerMode } from "../../workout/WorkoutDrawer";
import { defaultExerciseTitle } from "../../workout/utils";
import { LibraryPrivacy } from "@growth-machine-llc/stridist-api-client";
import { Sets } from "../../workout/types";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    "& .MuiInputBase-root": {
      height: 40,
      minHeight: "unset",
    },
  },

  autocomplete: {
    "&.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root": {
      paddingRight: "9px",
    },
  },

  textField: {},

  input: {
    fontSize: 13,
    fontWeight: 500,
    fontStyle: "normal",
  },

  noBorder: {
    border: "none",
  },

  option: {
    padding: theme.spacing(0.2, 0.2),
    fontSize: 13,
    fontWeight: 500,
  },

  iconWrapper: {
    display: "flex",
    alignItems: "center",
    color: theme.palette.common.black,
    marginRight: theme.spacing(-0.25),
  },
  listbox: {
    padding: theme.spacing(0.2, 2),
    boxShadow: "none",
  },
}));

interface Image {
  type?: "video" | "image";
  url: string;
  name?: string;
  cover?: boolean;
}

interface Asset {
  assetId?: string;
  title: string;
  subtitle: string;
  instructions: string;
  images: Image[];
  video?: string;
  sets?: Sets[];
  units?: string;
  typeExtraMeasurement?: ExerciseTypeExtra;
  typeSet?: ExerciseTypeSet;
  typeReps?: ExerciseTypeReps;
}

export default memo(
  ({
    initialValue,
    value,
    onValueChange,
    data,
    handleExerciseUpdate,
    api,
    rowIndex,
    column,
    setExerciseDrawerData,
    paramsData,
    setWorkoutDrawerMode,
    workoutDrawerMode,
  }: CustomCellEditorProps & CustomProgramCellEditorProps) => {
    const enableCallbackRef = useRef(false);
    const localOpenDrawer = useRef(false);
    const [isAutocompleteValueMatch, setIsAutocompleteValueMatch] =
      React.useState(false);

    const getAssetFromExercise = (e): Asset =>
      e && {
        assetId: e.assetId,
        title: e.title,
        subtitle: e.subtitle,
        instructions: e.instructions,
        images: e.images,
        video: e.video,
        sets: e.sets,
        units: e.units,
        typeExtraMeasurement: e.typeExtraMeasurement,
        typeSet: e.typeSet,
        typeReps: e.typeReps,
      };

    const inputRef = useRef<HTMLInputElement>(null);

    const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);

    const [asset, setAsset] = useState<Asset | null>(
      data.exerciseData.assetId
        ? getAssetFromExercise(data.exerciseData)
        : null,
    );

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, []);

    const s = useStyles();
    const theme = createTheme();

    const inputProps = {
      id: "workout-exercise-autocomplete",
      name: "title",
      placeholder: "ex. Weighted Crunch",
      fullWidth: true,
    };

    const handleAutocompleteChange = React.useCallback(
      (exerciseData: ExerciseAsset) => {
        if (!exerciseData) {
          return;
        }
        setAsset(
          getAssetFromExercise({
            ...exerciseData.exercise,
            assetId: exerciseData.assetId,
          }),
        );
      },
      [],
    );

    const handleAutocompleteTitleChange = (_, title: string, options) => {
      onValueChange(title);

      const matchingOption = options.find(
        (option) =>
          option?.exercise?.title?.toLowerCase() === title?.toLowerCase(),
      );
      if (matchingOption) {
        handleAutocompleteChange(matchingOption);
        setIsAutocompleteValueMatch(true);
      } else {
        setIsAutocompleteValueMatch(false);
      }
    };

    const handleCloseAutocomplete = useCallback(
      (options: ExerciseAsset[]) => {
        const matchingOption = options.find(
          (option) =>
            option?.exercise?.title?.toLowerCase() === value?.toLowerCase(),
        );
        if (matchingOption) {
          handleAutocompleteChange(matchingOption);
          setIsAutocompleteValueMatch(true);
        } else {
          setIsAutocompleteValueMatch(false);
        }
      },
      [handleAutocompleteChange, value],
    );

    const isCancelAfterEnd = useCallback(() => {
      const openDrawer = () => {
        const drawerData = {
          componentId: paramsData.componentId,
          componentContent: paramsData.componentContent,
          workoutSection: paramsData.workoutSectionData,
          exercise: {
            ...paramsData.exerciseData,
            ...asset,
            title:
              !isAutocompleteValueMatch && localOpenDrawer.current
                ? initialValue
                : value,
          },
        };
        setExerciseDrawerData(drawerData);
      };

      if (initialValue === value) {
        openDrawer();
        return true;
      }

      if (value === "" || !isAutocompleteValueMatch) {
        const rowNode = api!.getDisplayedRowAtIndex(rowIndex)!;
        api.flashCells({
          rowNodes: [rowNode],
          columns: [column],
        });

        if (!isAutocompleteValueMatch && localOpenDrawer.current) {
          openDrawer();
        }

        return true;
      }

      handleExerciseUpdate(
        {
          ...data.workoutSectionData,
          exercises: data.exercises.map((e) => ({
            ...e,
            ...(e.id === data.exerciseData.id && asset ? asset : {}),
            ...(e.id === data.exerciseData.id ? { title: value } : {}),
          })),
        },
        data.componentContent,
        data.workoutSectionData.id,
        data.componentId,
        enableCallbackRef.current ? openDrawer : undefined,
      );
      enableCallbackRef.current = false;

      return false;
    }, [
      value,
      isAutocompleteOpen,
      workoutDrawerMode,
      localOpenDrawer.current,
      isAutocompleteValueMatch,
    ]);

    useGridCellEditor({
      isCancelAfterEnd,
    });

    return (
      <div className={s.root}>
        <ExerciseAutocomplete
          classes={{
            root: s.autocomplete,
            option: s.option,
            listbox: s.listbox,
          }}
          renderCustomInput={(params: AutocompleteRenderInputParams) => (
            <TextField
              {...params}
              {...inputProps}
              variant="outlined"
              className={s.textField}
              inputRef={inputRef}
              slotProps={{
                input: {
                  ...params.InputProps,
                  className: s.input,
                  endAdornment: (
                    <>
                      <Box className={s.iconWrapper}>
                        <ChevronDown size={18} strokeWidth={1.75} />
                        <IconButton
                          size="small"
                          color="inherit"
                          onClick={(e) => {
                            enableCallbackRef.current = true;
                            isAutocompleteValueMatch
                              ? (localOpenDrawer.current = false)
                              : (localOpenDrawer.current = true);
                            api.stopEditing();
                            e.stopPropagation();
                            setWorkoutDrawerMode(
                              initialValue.length > 0 &&
                                value !== defaultExerciseTitle
                                ? WorkoutDrawerMode.COMPONENT_EXERCISE
                                : WorkoutDrawerMode.SEARCH_EXERCISE,
                            );
                          }}
                          sx={{
                            marginBottom: 0.2,
                          }}
                        >
                          <Video
                            size={22}
                            strokeWidth={1.2}
                            color={
                              isAutocompleteOpen
                                ? theme.palette.text.disabled
                                : theme.palette.common.black
                            }
                          />
                        </IconButton>
                      </Box>
                    </>
                  ),
                  classes: { notchedOutline: s.noBorder },
                },
              }}
            />
          )}
          onOpenChange={(open) => {
            setIsAutocompleteOpen(open);
          }}
          inputValue={
            value === getDefaultExerciseCellTitle(RowType.EXERCISE) &&
            initialValue === getDefaultExerciseCellTitle(RowType.EXERCISE)
              ? ""
              : value
          }
          onInputChange={handleAutocompleteTitleChange}
          onChange={handleAutocompleteChange}
          onCloseAutocomplete={handleCloseAutocomplete}
          disableAddNewExerciseOnMatching={true}
          onCreateNew={() => {
            localOpenDrawer.current = true;
            api.stopEditing();
            setWorkoutDrawerMode(WorkoutDrawerMode.LIBRARY_EXERCISE);
          }}
          privacy={LibraryPrivacy.PRIVATE}
        />
      </div>
    );
  },
);
